/**
 * Message Acknowledgment Service
 * 
 * Phase 2: Message Processing
 * 
 * Handles acknowledgment of received messages to the server,
 * tracks pending acknowledgments, and implements retry logic.
 * 
 * @author BitPanel Team
 * @date 2025-10-28
 */

/**
 * Acknowledgment status
 */
export const AckStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  CONFIRMED: 'CONFIRMED',
  FAILED: 'FAILED',
  TIMEOUT: 'TIMEOUT'
};

/**
 * Message Acknowledgment Manager
 */
export class MessageAcknowledgmentManager {
  constructor(options = {}) {
    this.maxRetries = options.maxRetries || 3;
    this.retryDelay = options.retryDelay || 1000; // 1 second
    this.ackTimeout = options.ackTimeout || 5000; // 5 seconds
    this.enabled = options.enabled !== false; // Enabled by default
    
    // Track pending acknowledgments
    // messageId -> { status, attempts, timestamp, timeoutId }
    this.pendingAcks = new Map();
    
    // Track acknowledgment statistics
    this.stats = {
      totalSent: 0,
      totalConfirmed: 0,
      totalFailed: 0,
      totalTimeout: 0,
      averageAckTime: 0,
      ackTimes: []
    };
  }
  
  /**
   * Send acknowledgment for a message
   * 
   * @param {Object} socket - Socket.io socket instance
   * @param {string|number} messageId - Message ID to acknowledge
   * @param {Object} metadata - Optional metadata to send with ack
   * @returns {Promise} Promise that resolves when ack is confirmed or rejects on failure
   */
  async sendAck(socket, messageId, metadata = {}) {
    if (!this.enabled) {
      console.log('📝 Message acknowledgment disabled - skipping');
      return Promise.resolve();
    }
    
    if (!socket || !socket.connected) {
      console.error('❌ Cannot send ack - socket not connected');
      return Promise.reject(new Error('Socket not connected'));
    }
    
    if (!messageId) {
      console.error('❌ Cannot send ack - no message ID provided');
      return Promise.reject(new Error('No message ID'));
    }
    
    // Check if already acknowledged
    if (this.pendingAcks.has(messageId)) {
      const existing = this.pendingAcks.get(messageId);
      if (existing.status === AckStatus.CONFIRMED) {
        console.log(`✅ Message ${messageId} already acknowledged`);
        return Promise.resolve();
      }
    }
    
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      
      // Create acknowledgment record
      const ackRecord = {
        status: AckStatus.PENDING,
        attempts: 0,
        timestamp: startTime,
        timeoutId: null,
        resolve: resolve,
        reject: reject
      };
      
      this.pendingAcks.set(messageId, ackRecord);
      
      // Attempt to send acknowledgment
      this._attemptAck(socket, messageId, metadata, ackRecord, startTime);
    });
  }
  
  /**
   * Attempt to send acknowledgment (with retry logic)
   * 
   * @private
   */
  _attemptAck(socket, messageId, metadata, ackRecord, startTime) {
    if (ackRecord.attempts >= this.maxRetries) {
      console.error(`❌ Max retries reached for message ${messageId}`);
      ackRecord.status = AckStatus.FAILED;
      this.stats.totalFailed++;
      this.pendingAcks.delete(messageId);
      ackRecord.reject(new Error('Max retries reached'));
      return;
    }
    
    ackRecord.attempts++;
    ackRecord.status = AckStatus.SENT;
    
    console.log(`📤 Sending ack for message ${messageId} (attempt ${ackRecord.attempts}/${this.maxRetries})`);
    
    // Send acknowledgment to server
    socket.emit('message_ack', {
      messageId: messageId,
      timestamp: Date.now(),
      ...metadata
    }, (response) => {
      // Server confirmed receipt
      if (response && response.success) {
        const ackTime = Date.now() - startTime;
        console.log(`✅ Ack confirmed for message ${messageId} (${ackTime}ms)`);
        
        ackRecord.status = AckStatus.CONFIRMED;
        this.stats.totalConfirmed++;
        this.stats.totalSent++;
        
        // Update average ack time
        this.stats.ackTimes.push(ackTime);
        if (this.stats.ackTimes.length > 100) {
          this.stats.ackTimes.shift(); // Keep only last 100
        }
        this.stats.averageAckTime = 
          this.stats.ackTimes.reduce((a, b) => a + b, 0) / this.stats.ackTimes.length;
        
        // Clear timeout
        if (ackRecord.timeoutId) {
          clearTimeout(ackRecord.timeoutId);
        }
        
        this.pendingAcks.delete(messageId);
        ackRecord.resolve();
      } else {
        console.warn(`⚠️ Ack failed for message ${messageId} - retrying...`);
        
        // Retry after delay
        setTimeout(() => {
          if (socket.connected) {
            this._attemptAck(socket, messageId, metadata, ackRecord, startTime);
          } else {
            console.error(`❌ Socket disconnected - cannot retry ack for message ${messageId}`);
            ackRecord.status = AckStatus.FAILED;
            this.stats.totalFailed++;
            this.pendingAcks.delete(messageId);
            ackRecord.reject(new Error('Socket disconnected'));
          }
        }, this.retryDelay);
      }
    });
    
    // Set timeout for this attempt
    ackRecord.timeoutId = setTimeout(() => {
      if (ackRecord.status !== AckStatus.CONFIRMED) {
        console.warn(`⏰ Ack timeout for message ${messageId} (attempt ${ackRecord.attempts})`);
        
        // Retry if attempts remaining
        if (ackRecord.attempts < this.maxRetries && socket.connected) {
          this._attemptAck(socket, messageId, metadata, ackRecord, startTime);
        } else {
          ackRecord.status = AckStatus.TIMEOUT;
          this.stats.totalTimeout++;
          this.pendingAcks.delete(messageId);
          ackRecord.reject(new Error('Acknowledgment timeout'));
        }
      }
    }, this.ackTimeout);
  }
  
  /**
   * Handle acknowledgment confirmation from server
   * 
   * @param {string|number} messageId - Message ID that was confirmed
   */
  handleAckConfirmation(messageId) {
    const ackRecord = this.pendingAcks.get(messageId);
    if (ackRecord) {
      console.log(`✅ Received ack confirmation for message ${messageId}`);
      ackRecord.status = AckStatus.CONFIRMED;
      
      if (ackRecord.timeoutId) {
        clearTimeout(ackRecord.timeoutId);
      }
      
      this.pendingAcks.delete(messageId);
      
      if (ackRecord.resolve) {
        ackRecord.resolve();
      }
    }
  }
  
  /**
   * Get pending acknowledgments
   * 
   * @returns {Array} Array of pending message IDs
   */
  getPendingAcks() {
    return Array.from(this.pendingAcks.keys());
  }
  
  /**
   * Clear all pending acknowledgments
   */
  clearPendingAcks() {
    for (const [messageId, ackRecord] of this.pendingAcks.entries()) {
      if (ackRecord.timeoutId) {
        clearTimeout(ackRecord.timeoutId);
      }
      if (ackRecord.reject) {
        ackRecord.reject(new Error('Acknowledgments cleared'));
      }
    }
    this.pendingAcks.clear();
    console.log('🗑️ Cleared all pending acknowledgments');
  }
  
  /**
   * Get acknowledgment statistics
   * 
   * @returns {Object} Statistics object
   */
  getStats() {
    const totalAttempts = this.stats.totalSent + this.stats.totalFailed + this.stats.totalTimeout;
    
    return {
      totalSent: this.stats.totalSent,
      totalConfirmed: this.stats.totalConfirmed,
      totalFailed: this.stats.totalFailed,
      totalTimeout: this.stats.totalTimeout,
      pending: this.pendingAcks.size,
      successRate: totalAttempts > 0
        ? ((this.stats.totalConfirmed / totalAttempts) * 100).toFixed(2) + '%'
        : '0%',
      averageAckTime: Math.round(this.stats.averageAckTime) + 'ms'
    };
  }
  
  /**
   * Reset statistics
   */
  resetStats() {
    this.stats = {
      totalSent: 0,
      totalConfirmed: 0,
      totalFailed: 0,
      totalTimeout: 0,
      averageAckTime: 0,
      ackTimes: []
    };
    console.log('📊 Reset acknowledgment statistics');
  }
  
  /**
   * Enable acknowledgments
   */
  enable() {
    this.enabled = true;
    console.log('✅ Message acknowledgments enabled');
  }
  
  /**
   * Disable acknowledgments
   */
  disable() {
    this.enabled = false;
    this.clearPendingAcks();
    console.log('❌ Message acknowledgments disabled');
  }
}

